/**
 * FormWizardHeaderComponent — the shared chrome atop the two-step form-builder
 * editors (registration forms, assessment templates): a back link, the title
 * with an optional status badge, and a "Step X of N" subtitle with the current
 * step's label, plus a right-aligned action bar.
 *
 * Presentational only. The status badge and the step navigation / save buttons
 * are feature-specific and project in via the `[wizard-badge]` and
 * `[wizard-actions]` slots — this component owns layout, not behaviour.
 */
import { Component, computed, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'form-wizard-header',
  imports: [RouterLink, MatIcon],
  host: { class: 'flex flex-col gap-y-6' },
  template: `
    <!-- Back -->
    <div>
      <a
        class="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        [routerLink]="backLink()"
      >
        <mat-icon svgIcon="arrow-left" class="size-4" />
        {{ backLabel() }}
      </a>
    </div>

    <!-- Title + step subtitle + actions -->
    <div
      class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-xl font-semibold tracking-tighter sm:text-2xl">
            {{ title() }}
          </h1>
          <ng-content select="[wizard-badge]" />
        </div>
        @if (showSteps()) {
          <p class="mt-1 text-sm text-neutral-500">
            Step {{ step() }} of {{ totalSteps() }} — {{ currentStepLabel() }}
          </p>
        } @else if (subtitle()) {
          <p class="mt-1 text-sm text-neutral-500">{{ subtitle() }}</p>
        }
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <ng-content select="[wizard-actions]" />
      </div>
    </div>
  `,
})
export class FormWizardHeaderComponent {
  readonly backLink = input.required<string | unknown[]>();
  readonly backLabel = input('Back');
  readonly title = input.required<string>();
  readonly step = input.required<number>();
  readonly totalSteps = input(2);
  /** Human labels for each step, indexed 1..N. */
  readonly stepLabels = input<string[]>([]);
  /**
   * Show the "Step X of N" subtitle. Off for an editor that navigates by tabs
   * rather than in sequence — an existing form isn't built front-to-back, and
   * calling its sections "steps" made panels like Responses look like part of
   * a build flow.
   */
  readonly showSteps = input(true);
  /** Replaces the step subtitle when `showSteps` is off. */
  readonly subtitle = input('');

  protected readonly currentStepLabel = computed(
    () => this.stepLabels()[this.step() - 1] ?? '',
  );
}
